import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBeforeUnload, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { confirmAlert } from 'components/common';
import {
  batchUpdateAppSettings, deleteLogo, getAppSettings, resetAppSetting, seedFromEnv,
  syncOplaContractTypes, syncOplaCustomFields, syncOplaRelatedPartyRoles,
  syncOplaSalesProcess, syncOplaTaskTypes, uploadLogo,
} from 'redux/appSettingsSlice';
import {
  FILTERS, LOGO_KEY, isJsonSetting, isUnset, jsonError, matchesFilter, matchesQuery, prettyJson,
} from './constants';

// Keys whose value is fetched from Opla rather than typed.
const SYNC_THUNKS = {
  OPLA_SALES_PROCESS:      syncOplaSalesProcess,
  OPLA_CUSTOM_FIELDS:      syncOplaCustomFields,
  OPLA_CONTRACT_TYPE:      syncOplaContractTypes,
  OPLA_TASK_TYPE:          syncOplaTaskTypes,
  OPLA_RELATED_PARTY_ROLE: syncOplaRelatedPartyRoles,
};

/**
 * Everything this screen knows and can do. The components below it render; this
 * holds the pending edits, the filters, and the calls.
 *
 * Where you are (group, sub-group, search, filter) lives in the URL rather than
 * in component state: setting a portal up is a two-person job, and "open
 * Settings, CRM group, Contract" is then a link somebody can send.
 */
const useAppSettings = () => {
  const dispatch = useDispatch();
  const { data, groups, sub_group_labels: subGroupLabels, loading } = useSelector((s) => s.app_settings);

  const [params, setParams] = useSearchParams();
  const group    = params.get('group') || 'all';
  const subGroup = params.get('sub') || null;
  const search   = params.get('q') || '';
  const filter   = FILTERS.some((f) => f.key === params.get('filter')) ? params.get('filter') : 'all';

  const setParam = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === '' || value === 'all') next.delete(key);
        else next.set(key, value);
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const [pending, setPending] = useState({});
  // The first fetch has resolved (either way). Without it the screen shows its
  // "nothing here, seed from .env" empty state for one frame on a cold load,
  // which is the one message that must never appear when it is not true.
  const [settled, setSettled] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [syncingKey, setSyncingKey] = useState(null);
  const searchRef = useRef(null);

  const pendingKeys = Object.keys(pending);
  const hasPending  = pendingKeys.length > 0;

  useEffect(() => {
    dispatch(getAppSettings()).finally(() => setSettled(true));
  }, [dispatch]);

  useBeforeUnload(useCallback((e) => { if (hasPending) e.preventDefault(); }, [hasPending]));

  // A value that came back from the server matching what is pending is no
  // longer a change — this is what clears the bar after a save.
  useEffect(() => {
    setPending((prev) => {
      const next = { ...prev };
      data.forEach((s) => {
        const server = isJsonSetting(s) ? prettyJson(s.value || '') : (s.value || '');
        if (next[s.key] !== undefined && next[s.key] === server) delete next[s.key];
      });
      return next;
    });
  }, [data]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const handleValueChange = useCallback((key, value, reverted) => {
    setPending((prev) => {
      const next = { ...prev };
      if (reverted) delete next[key]; else next[key] = value;
      return next;
    });
  }, []);

  const handleRevert = useCallback((key) => {
    setPending((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleReset = useCallback(async (key) => {
    await dispatch(resetAppSetting(key)).unwrap();
    handleRevert(key);
  }, [dispatch, handleRevert]);

  const handleSave = useCallback(async () => {
    const invalidKeys = Object.entries(pending)
      .filter(([key, value]) => {
        const setting = (data || []).find((s) => s.key === key);
        return setting && isJsonSetting(setting) && jsonError(value);
      })
      .map(([key]) => key);
    if (invalidKeys.length > 0) {
      toast.error(`Not valid JSON: ${invalidKeys.join(', ')}. Fix it before saving.`);
      return;
    }

    setSaving(true);
    try {
      const payload = Object.entries(pending).map(([key, value]) => ({ key, value }));
      await dispatch(batchUpdateAppSettings(payload)).unwrap();
      setPending({});
    } finally {
      setSaving(false);
    }
  }, [dispatch, pending, data]);

  const handleDiscard = useCallback(() => confirmAlert({
    title: 'Discard changes',
    message: `Discard <strong>${pendingKeys.length}</strong> unsaved change${pendingKeys.length !== 1 ? 's' : ''}?`,
    type: 'danger',
    buttons: [
      { label: 'Discard', variant: 'danger', onClick: () => { setPending({}); dispatch(getAppSettings()); } },
      { label: 'Cancel', onClick: () => {} },
    ],
  }), [dispatch, pendingKeys.length]);

  const handleSeed = useCallback(async () => {
    setSeeding(true);
    try {
      await dispatch(seedFromEnv()).unwrap();
      await dispatch(getAppSettings());
    } finally {
      setSeeding(false);
    }
  }, [dispatch]);

  const handleSync = useCallback(async (key) => {
    const thunk = SYNC_THUNKS[key];
    if (!thunk) return;

    setSyncingKey(key);
    try {
      await dispatch(thunk()).unwrap();
    } finally {
      setSyncingKey(null);
    }
  }, [dispatch]);

  const handleUploadLogo = useCallback(async (file) => {
    await dispatch(uploadLogo(file)).unwrap();
    await dispatch(getAppSettings());
  }, [dispatch]);

  const handleDeleteLogo = useCallback(() => confirmAlert({
    title: 'Remove logo',
    message: 'Remove the application logo? The portal header falls back to the default immediately.',
    type: 'danger',
    buttons: [
      { label: 'Remove', variant: 'danger', onClick: () => dispatch(deleteLogo()).unwrap() },
      { label: 'Cancel', onClick: () => {} },
    ],
  }), [dispatch]);

  // ── Keyboard ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const onKeyDown = (event) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName);

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        if (!hasPending || saving) return;
        event.preventDefault();
        handleSave();
        return;
      }
      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleSave, hasPending, saving]);

  // ── Derived views ───────────────────────────────────────────────────────────

  const settings = useMemo(() => data.filter((s) => s.key !== LOGO_KEY), [data]);
  const logoSetting = useMemo(() => data.find((s) => s.key === LOGO_KEY), [data]);

  // Counts are for what the group/search selection already narrowed to, so a
  // chip never promises rows that are not there.
  const inScope = useMemo(() => {
    const q = search.toLowerCase().trim();
    return settings.filter((s) => {
      if (group !== 'all' && s.group !== group) return false;
      if (subGroup && s.sub_group !== subGroup) return false;
      return matchesQuery(s, q);
    });
  }, [settings, group, subGroup, search]);

  const counts = useMemo(() => ({
    all:        inScope.length,
    unset:      inScope.filter((s) => isUnset(s)).length,
    overridden: inScope.filter((s) => s.overridden).length,
    sensitive:  inScope.filter((s) => s.is_sensitive).length,
    dirty:      inScope.filter((s) => pending[s.key] !== undefined).length,
  }), [inScope, pending]);

  const visible = useMemo(
    () => inScope.filter((s) => matchesFilter(s, filter, pending[s.key] !== undefined)),
    [inScope, filter, pending],
  );

  // Flat, typed rows so the list renders in one pass:
  // { type: 'group' | 'subgroup' | 'setting' }
  const rows = useMemo(() => {
    const out = [];
    const byGroup = new Map();
    visible.forEach((s) => {
      if (!byGroup.has(s.group)) byGroup.set(s.group, []);
      byGroup.get(s.group).push(s);
    });

    byGroup.forEach((items, groupKey) => {
      out.push({ type: 'group', key: groupKey, count: items.length });

      // Sub-groups only earn a divider when the group is open and actually
      // splits — in the "All settings" view they would be noise.
      const splits = group !== 'all' && !subGroup && items.some((s) => s.sub_group);
      if (!splits) {
        items.forEach((s) => out.push({ type: 'setting', setting: s }));
        return;
      }

      const bySub = new Map();
      items.forEach((s) => {
        const key = s.sub_group || '';
        if (!bySub.has(key)) bySub.set(key, []);
        bySub.get(key).push(s);
      });

      (bySub.get('') || []).forEach((s) => out.push({ type: 'setting', setting: s }));

      const ordered = [
        ...Object.keys(subGroupLabels).filter((k) => bySub.has(k)),
        ...[...bySub.keys()].filter((k) => k && !subGroupLabels[k]),
      ];
      ordered.forEach((subKey) => {
        out.push({ type: 'subgroup', key: subKey, count: bySub.get(subKey).length });
        bySub.get(subKey).forEach((s) => out.push({ type: 'setting', setting: s }));
      });
    });

    return out;
  }, [visible, group, subGroup, subGroupLabels]);

  return {
    data, settings, logoSetting, groups, subGroupLabels, loading, settled,
    group, subGroup, search, filter, searchRef,
    setGroup: (next) => setParam({ group: next, sub: null }),
    setSubGroup: (next) => setParam({ sub: next }),
    setSearch: (next) => setParam({ q: next }),
    setFilter: (next) => setParam({ filter: next }),
    pending, pendingKeys, hasPending, counts, rows, visibleCount: visible.length,
    saving, seeding, syncingKey,
    handleValueChange, handleRevert, handleReset, handleSave, handleDiscard,
    handleSeed, handleSync, handleUploadLogo, handleDeleteLogo,
    refresh: () => dispatch(getAppSettings()),
  };
};

export default useAppSettings;
